<template>
  <q-dialog v-model="visible" persistent>
    <q-card class="modal-card">
      <div class="modal-header">
        <q-card-section class="header">
          <div class="header-content-wrapper">
            <div class="header-title">{{ title || 'Pontos de Coleta' }}</div>
            <q-btn icon="close" flat round color="white" @click="closeModal" class="header-close-btn" />
          </div>
        </q-card-section>
      </div>

      <div class="modal-body">
        <!-- Seção de Pesquisa -->
        <div class="search-section">
          <h3 class="section-title"><translate>INDUSTRIAL_MAP_SEARCH</translate></h3>
          <div class="search-form">
            <div class="search-fields" :style="searchFieldsGridStyle">
              <!-- Campos de filtro dinâmicos baseados nos forms -->
              <div
                v-for="input in searchInputs"
                :key="input.name"
                class="search-field"
              >
                <!-- Campo de Data com DatePicker -->
                <q-input
                  v-if="input.type && input.type.toUpperCase() === 'DATE'"
                  v-model="searchData[input.name]"
                  v-bind="getFieldProps(input)"
                  class="search-input date-range-input"
                >
                  <template v-slot:append>
                    <q-icon name="event" class="cursor-pointer">
                      <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                        <q-date
                          v-model="searchData[input.name]"
                          mask="DD/MM/YYYY"
                          @input="() => $refs[`popup_${input.name}`].hide()"
                        >
                          <div class="row items-center justify-end">
                            <q-btn v-close-popup :label="$gettext('INDUSTRIAL_MAP_BTN_CLOSE')" color="primary" flat />
                          </div>
                        </q-date>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>

                <!-- Campo de Date Range com DatePicker -->
                <q-input
                  v-else-if="input.type && input.type.toUpperCase() === 'DATE_RANGE'"
                  :value="getDateRangeDisplayValue(input.name)"
                  v-bind="getFieldProps(input)"
                  readonly
                  class="search-input date-range-input"
                >
                  <template v-slot:append>
                    <q-icon name="event" class="cursor-pointer">
                      <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                        <div class="q-pa-md">
                          <div class="q-pb-sm">
                            Período: {{ getDateRangeDisplayValue(input.name) || 'Selecione o período' }}
                          </div>
                          <q-date
                            v-model="searchData[input.name]"
                            range
                            mask="DD/MM/YYYY"
                          />
                          <div class="row items-center justify-end q-pt-md">
                            <q-btn v-close-popup :label="$gettext('INDUSTRIAL_MAP_BTN_CLOSE')" color="primary" flat />
                          </div>
                        </div>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>

                <!-- Campo SELECT/COMBO com autocomplete -->
                <q-select
                  v-else-if="input.type && (input.type.toUpperCase() === 'SELECT' || input.type.toUpperCase() === 'COMBO')"
                  v-model="searchData[input.name]"
                  v-bind="getFieldProps(input)"
                  :options="getFilteredOptions(input)"
                  @filter="(val, update) => onFilterSelect(val, update, input)"
                  class="search-input"
                >
                  <template v-slot:no-option>
                    <q-item>
                      <q-item-section class="text-grey">
                        Nenhum resultado encontrado
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>

                <!-- Outros tipos de campo -->
                <component
                  v-else
                  :is="getFieldComponent(input)"
                  v-model="searchData[input.name]"
                  v-bind="getFieldProps(input)"
                  class="search-input"
                />
              </div>
            </div>
            <div class="search-actions">
              <q-btn
                color="primary"
                @click="performSearch"
                :loading="isSearching"
                class="search-btn"
              >
                <q-icon name="search" class="q-mr-sm" />
                Pesquisar
              </q-btn>
            </div>
          </div>
        </div>

        <!-- Seção de Resultados -->
        <div class="results-section" :class="{ 'hide-expand-column': hideExpandButton }">
          <!-- Filtro de Dias (Checkboxes) - substitui o título da tabela -->
          <div v-if="showDayFilter" class="day-filter-section">
            <span class="day-filter-label">{{ dayFilterLabel }}</span>
            <div class="day-filter-checkboxes">
              <q-checkbox
                v-for="option in dayFilterOptionsComputed"
                :key="option.value"
                :value="isDayFilterChecked(option.value)"
                :label="option.label"
                dense
                @click.native.prevent="onDayCheckboxClick(option.value)"
              />
            </div>
          </div>

          <jt-data-table
            :key="datatableKey"
            ref="tableRef"
            :api-mode="apiMode"
            :nm-interface="nmInterface"
            :fields="columns"
            :extra-params="computedExtraParams"
            :buttons="buttons"
            :rows-per-page-initial="rowsPerPageInitial"
            :rows-per-page-options="rowsPerPageOptions"
            :virtual-scroll="virtualScroll"
            :row-key="rowKey"
            :message-data-empty="messageDataEmpty || $gettext('NO_DATA_AVAILABLE')"
            :image-column-not-found="imageColumnNotFound"
            :pre-selected-keys="preSelectedKeys"
            :data="data"
            :default-http-options="defaultHttpOptions"
            :forms="forms"
            :is-paginated="isPaginated"
            :title="showDayFilter ? '' : title"
            :column-edit="columnEdit"
            :bottom-edit="bottomEdit"
            :row-set="rowSet"
            :buttons-filter="buttonsFilter"
            :singleselect="singleselect"
            :multiselect="multiselect"
            :noneselect="noneselect"
            :selection-force="selectionForce"
            :row-buttons="rowButtons"
            @action-button="onActionButtonHandler"
            @click-row="onClickRowHandler"
            @show-message="showMessageHandler"
            @toggle-change="(data) => $emit('toggle-change', data)"
          >
            <!-- Slot para conteúdo de expansão customizado -->
            <template v-slot:custom-expand-content="{ row, index }">
              <slot
                name="custom-expand-content"
                :row="row"
                :index="index"
              />
            </template>

            <!-- Passar outros slots dinamicamente incluindo body-cell-actions -->
            <template
              v-for="(_, key) in $scopedSlots"
              v-slot:[key]="props"
            >
              <slot
                v-if="key !== 'custom-expand-content'"
                :name="key"
                v-bind="props"
              />
            </template>
          </jt-data-table>
        </div>
      </div>

      <!-- Modal Footer com botão de fechar -->
      <div class="modal-footer">
        <q-card-actions align="center" class="footer">
          <q-btn :label="$gettext('INDUSTRIAL_MAP_BTN_CLOSE')" color="grey-7" class="btn-fechar" @click="closeModal" />
        </q-card-actions>
      </div>
    </q-card>
  </q-dialog>
</template>
<script>
import { Component, Vue, Watch } from 'vue-property-decorator'
import { JtDataTable } from 'jtech-webcomps'

@Component({
  components: {
    JtDataTable
  },
  props: {
    // Props específicas da modal
    value: {
      type: Boolean,
      default: false
    },
    outpostId: {
      type: [String, Number],
      required: false
    },

    // Props originais
    columnEdit: {
      type: Function
    },
    bottomEdit: {
      type: Function
    },
    rowSet: {
      type: Function
    },
    selectionForce: {
      type: Boolean,
      default: false
    },
    singleselect: {
      type: Boolean
    },
    multiselect: {
      type: Boolean
    },
    buttons: {
      required: false,
      type: Array,
      default: () => []
    },
    rowButtons: {
      required: false,
      type: Boolean,
      default: false
    },
    rowsPerPageInitial: {
      type: Number,
      default: 5
    },
    buttonsFilter: {
      type: Function
    },
    noneselect: {
      type: Boolean
    },

    /**
     * @Example
     * [{
        name: 'birthdate',
        field: 'birthdate',
        sortable: true,
        label:'Birthday Date'
      }]
     */
    fields: {
      type: Array,
      required: true,
      default: () => []
    },
    extraParams: {
      type: Object,
      default: () => {
        return {}
      }
    },
    defaultHttpOptions: Object,
    nmInterface: String,
    rowKey: {
      type: String,
      default: 'id'
    },
    title: String,
    isPaginated: {
      type: Boolean,
      default: true
    },
    forms: { type: Array },
    apiMode: {
      type: Boolean,
      default: true
    },
    data: {
      type: Array,
      default: () => []
    },
    messageDataEmpty: {
      type: String,
      default: ''
    },
    rowsPerPageOptions: {
      type: Array,
      default: () => [3, 5, 7, 10, 15, 20, 25, 50, 0]
    },
    preSelectedKeys: {
      type: Array,
      default: () => []
    },
    virtualScroll: Boolean,
    imageColumnNotFound: String,
    relacionamentos: {
      type: Object,
      default: () => ({})
    },
    // Props para controlar slot de ações customizadas
    customActionSlot: {
      type: Boolean,
      default: false
    },
    hideExpandButton: {
      type: Boolean,
      default: false
    },
    // Número de campos por linha no filtro (padrão: 4)
    fieldsPerRow: {
      type: Number,
      default: 4
    },
    // Exibir filtro de dias (radio buttons 7/15/30 dias)
    showDayFilter: {
      type: Boolean,
      default: false
    },
    // Label exibido ao lado dos radio buttons
    dayFilterLabel: {
      type: String,
      default: ''
    },
    // Opções do filtro de dias (array de números)
    dayFilterOptions: {
      type: Array,
      default: () => [7, 15, 30]
    },
    // Valor inicial do filtro de dias
    dayFilterDefault: null
  }
})
class ModalFiltrosDinamicos extends Vue {
  // Novas propriedades para a modal
  searchData = {};
  isSearching = false;
  filteredOptionsMap = {};
  datatableKey = 0;
  selectedDayFilter = null;

  // Computed property para estilo dinâmico do grid de campos de filtro
  get searchFieldsGridStyle () {
    if (this.fieldsPerRow && this.fieldsPerRow > 0) {
      return {
        'grid-template-columns': `repeat(${this.fieldsPerRow}, 1fr)`
      }
    }
    return {}
  }

  // Computed property para as opções do filtro de dias
  get dayFilterOptionsComputed () {
    return this.dayFilterOptions.map(days => ({
      label: `Últimos ${days} dias`,
      value: days
    }))
  }

  // Computed para extra-params incluindo o filtro de dias via scheduledDate
  get computedExtraParams () {
    // Filtrar searchData para remover valores nulos/vazios, evitando enviar
    // parâmetros vazios ao backend que causam retorno sem resultados
    const filteredSearchData = {}
    if (this.searchData) {
      Object.keys(this.searchData).forEach(key => {
        const val = this.searchData[key]
        if (val !== null && val !== undefined && val !== '') {
          filteredSearchData[key] = val
        }
      })
    }
    const params = { ...this.extraParams, ...filteredSearchData }
    if (this.showDayFilter && this.selectedDayFilter) {
      const today = new Date()
      const startDate = new Date()
      startDate.setDate(today.getDate() - this.selectedDayFilter)

      // endDate = amanhã, pois scheduledDate tem hora (DataDMA_HM)
      // e o filtro usa <= (DataDMA sem hora = 00:00), excluindo registros de hoje
      const endDate = new Date()
      endDate.setDate(today.getDate() + 1)

      // Formatar para DD/MM/YYYY (padrão do backend)
      const formatDate = (date) => {
        const dd = String(date.getDate()).padStart(2, '0')
        const mm = String(date.getMonth() + 1).padStart(2, '0')
        const yyyy = date.getFullYear()
        return `${dd}/${mm}/${yyyy}`
      }

      params.startScheduledDate = formatDate(startDate)
      params.endScheduledDate = formatDate(endDate)
    }
    return params
  }

  // Verifica se um checkbox de dias está marcado
  isDayFilterChecked (days) {
    return this.selectedDayFilter === days
  }

  // Handler para click no checkbox (toggle manual)
  onDayCheckboxClick (days) {
    if (this.selectedDayFilter === days) {
      // Desmarcar: remover filtro de dias
      this.selectedDayFilter = null
    } else {
      // Marcar este, desmarcar outros
      this.selectedDayFilter = days
    }
    this.$emit('day-filter-change', this.selectedDayFilter)
    // Forçar remontagem do datatable para buscar dados filtrados
    this.datatableKey++
  }

  // Computed property para v-model
  get visible () {
    return this.value
  }

  set visible (val) {
    this.$emit('input', val)
  }

  mounted () {
    // Inicializar filtro de dias com o valor padrão (null = sem filtro)
    this.selectedDayFilter = this.dayFilterDefault || null
    // initializeSearchData será chamado pelo watcher dos forms
    this.loadSelectOptions()
  }

  // Watch para customActionSlot
  @Watch('customActionSlot')
  onCustomActionSlotChanged (newVal, oldVal) {
    // Slot customizado mudou
  }

  // Watch para hideExpandButton
  @Watch('hideExpandButton')
  onHideExpandButtonChanged (newVal, oldVal) {
    // hideExpandButton mudou
  }

  // Watch para reagir a mudanças nos forms
  @Watch('forms', { immediate: true })
  onFormsChanged (newForms) {
    if (newForms && newForms.length > 0) {
      this.$nextTick(() => {
        this.initializeSearchData()
        this.loadSelectOptions()
      })
    }
  }

  // Watch para reagir a mudanças nos relacionamentos
  @Watch('relacionamentos', { immediate: true, deep: true })
  onRelacionamentosChanged (newRelacionamentos) {
    if (newRelacionamentos && Object.keys(newRelacionamentos).length > 0) {
      this.$nextTick(() => {
        this.loadSelectOptions()
      })
    }
  }

  // ========================================
  // MÉTODOS DA MODAL
  // ========================================

  closeModal () {
    this.$emit('close')
  }

  async performSearch () {
    this.isSearching = true
    try {
      // Incrementar a key força o datatable a remontar com estado limpo,
      // prevenindo travamento no loading quando o __request do framework falha sem try-catch
      this.datatableKey++

      this.$emit('search-completed', { searchData: this.searchData })
    } catch (error) {
      console.error('[ModalFiltrosDinamicos] Erro na pesquisa:', error)
      this.$emit('search-error', error)
    } finally {
      this.isSearching = false
    }
  }

  refresh (params) {
    if (params) {
      // Os parâmetros de pesquisa já estão sendo passados via extra-params
    }
    this.$refs?.tableRef?.refresh()
  }

  // Método para emitir evento de ação customizada
  onCustomActionClick (row, actionType, additionalData = {}) {
    this.$emit('custom-action', {
      row,
      actionType,
      ...additionalData
    })
  }

  // Handlers para repassar eventos do JtDataTable
  onActionButtonHandler (button, params, context) {
    this.$emit('action-button', button, params, context)
  }

  onClickRowHandler (payload, context) {
    this.$emit('click-row', payload, context)
  }

  showMessageHandler (payload, param, context) {
    this.$emit('show-message', payload, param, context)
  }

  getFieldComponent (field) {
    // Normalizar o tipo para maiúsculas para comparação
    const fieldType = field.type ? field.type.toUpperCase() : 'TEXT'

    // Determina o componente baseado no tipo do campo
    switch (fieldType) {
      case 'SELECT':
      case 'COMBO':
        return 'q-select'
      case 'DATE':
        return 'q-input' // Usar q-input com ícone para mostrar popup de data
      case 'DATE_RANGE':
        return null // DATE_RANGE é tratado diretamente no template
      case 'TEXTAREA':
        return 'q-textarea'
      case 'NUMBER':
      case 'INTEGER':
        return 'q-input'
      case 'TEXT':
      default:
        return 'q-input'
    }
  }

  getFieldProps (field) {
    // Normalizar o tipo para maiúsculas para comparação
    const fieldType = field.type ? field.type.toUpperCase() : 'TEXT'

    const baseProps = {
      outlined: false,
      filled: false,
      standout: false,
      borderless: false,
      dense: false,
      clearable: true,
      label: field.label,
      'stack-label': false,
      'float-label': true,
      'hide-hint': true
    }

    switch (fieldType) {
      case 'SELECT':
      case 'COMBO':
        return {
          ...baseProps,
          options: field.options || [],
          'option-label': field.optionLabel || 'label',
          'option-value': field.optionValue || 'value',
          'emit-value': true,
          'map-options': true,
          'use-input': true,
          'fill-input': false,
          'hide-selected': false,
          filterable: true,
          loading: !field.options || field.options.length === 0
        }
      case 'NUMBER':
      case 'INTEGER':
        return {
          ...baseProps,
          type: 'number',
          inputmode: 'numeric'
        }
      case 'DATE':
        return {
          ...baseProps,
          mask: '##/##/####',
          readonly: false
        }
      case 'DATE_RANGE':
        return {
          ...baseProps
        }
      case 'TEXTAREA':
        return {
          ...baseProps,
          type: 'textarea',
          rows: 3
        }
      case 'TEXT':
      default:
        return {
          ...baseProps,
          type: 'text'
        }
    }
  }

  // Inicializa dados de pesquisa baseados nos forms
  initializeSearchData () {
    if (this.searchInputs && this.searchInputs.length > 0) {
      const searchData = {}
      this.searchInputs.forEach(input => {
        // Inicializa com valor padrão ou null/valor vazio baseado no tipo
        switch (input.type ? input.type.toUpperCase() : 'TEXT') {
          case 'SELECT':
          case 'COMBO':
            searchData[input.name] = null
            break
          case 'NUMBER':
          case 'INTEGER':
            searchData[input.name] = null
            break
          case 'DATE':
            searchData[input.name] = ''
            break
          case 'DATE_RANGE':
            searchData[input.name] = null // Para range, usar null inicialmente
            break
          default:
            searchData[input.name] = ''
        }
      })
      this.searchData = searchData
    } else {
      this.searchData = {}
    }
  }

  // Método para exibir o valor do date range no input
  getDateRangeDisplayValue (fieldName) {
    const rangeValue = this.searchData[fieldName]
    if (!rangeValue) return ''

    if (typeof rangeValue === 'object' && rangeValue.from && rangeValue.to) {
      return `${rangeValue.from} - ${rangeValue.to}`
    }

    if (typeof rangeValue === 'string' && rangeValue.includes('/')) {
      return rangeValue
    }

    return ''
  }

  // Carrega opções para campos do tipo SELECT/COMBO usando apenas os relacionamentos
  async loadSelectOptions () {
    if (!this.searchInputs || this.searchInputs.length === 0) return

    const selectFields = this.searchInputs.filter(input => {
      const fieldType = input.type ? input.type.toUpperCase() : 'TEXT'
      return fieldType === 'SELECT' || fieldType === 'COMBO'
    })

    for (const field of selectFields) {
      try {
        // Usar relacionamentos passados pelo pai
        if (this.relacionamentos && Object.keys(this.relacionamentos).length > 0) {
          // Usar dataName como chave primária para buscar no relacionamento
          const relationshipKey = field.dataName || field.name

          if (this.relacionamentos[relationshipKey] && Array.isArray(this.relacionamentos[relationshipKey])) {
            const relacionamentoData = this.relacionamentos[relationshipKey]

            // Mapear dados do relacionamento para o formato esperado pelo q-select
            field.options = relacionamentoData.map(item => ({
              label: this._resolveRelacionamentoLabel(item),
              value: item.value || item.id
            }))

            continue // Pula para o próximo campo
          } else {
          }
        }

        // Se não encontrou nos relacionamentos, inicializar como array vazio
        if (!field.options || field.options.length === 0) {
          field.options = []
        }
      } catch (error) {
        console.warn(`[ModalFiltrosDinamicos] Erro ao carregar opções para ${field.name}:`, error)
        // Em caso de erro, inicializar como array vazio
        field.options = []
      }
    }
  }

  /**
   * Resolve o label de um item de relacionamento.
   * O framework pode retornar labels no formato de chave i18n (ex: "taskStatusType.nmDescricao.NOEXECUTADA")
   * quando a tradução não existe no -msg.properties. Nesse caso, extrai a parte legível após "nmDescricao."
   * e formata como texto humano (ex: "Não Executada").
   */
  _resolveRelacionamentoLabel (item) {
    const rawLabel = item.label || item.nome || item.name || item.description || `${item.value}`
    if (!rawLabel || typeof rawLabel !== 'string') return rawLabel

    // Detectar padrão de chave i18n do framework: "xxx.nmDescricao.VALOR"
    const nmDescricaoPattern = /\.nmDescricao\.(.+)$/
    const match = rawLabel.match(nmDescricaoPattern)
    if (match) {
      // Extrair o código e formatar: "NOEXECUTADA" -> "Não Executada", "EMPROGRESSO" -> "Em Progresso"
      const code = match[1]
      return this._formatStatusCode(code)
    }

    return rawLabel
  }

  /**
   * Formata código de status para texto legível.
   * Ex: "NOEXECUTADA" -> "Não Executada", "EMPROGRESSO" -> "Em Progresso"
   */
  _formatStatusCode (code) {
    const statusMap = {
      NOEXECUTADA: 'Não Executada',
      EMPROGRESSO: 'Em Progresso',
      COMPLETADA: 'Completada',
      CANCELADA: 'Cancelada',
      AGUARDANDOAPROVAO: 'Aguardando Aprovação',
      PESAGEMPENDENTE: 'Pesagem Pendente',
      CONFIRMADO: 'Confirmado',
      JUSTIFICATIVA: 'Justificativa',
      SOLICITADA: 'Solicitada',
      PROGRAMADA: 'Programada',
      COLETADA: 'Coletada',
      NODEFINIDA: 'Não Definida'
    }
    // Se encontrou no mapa, retorna o texto. Senão, retorna o código original.
    return statusMap[code] || code
  }

  // ========================================
  // MÉTODOS DE AUTOCOMPLETE / FILTRO
  // ========================================

  getFilteredOptions (input) {
    const key = input.dataName || input.name
    if (this.filteredOptionsMap[key] !== undefined) {
      return this.filteredOptionsMap[key]
    }
    return input.options || []
  }

  onFilterSelect (val, update, input) {
    const key = input.dataName || input.name
    const allOptions = input.options || []
    const optionLabel = input.optionLabel || 'label'

    update(() => {
      if (!val || val.trim() === '') {
        this.$set(this.filteredOptionsMap, key, allOptions)
      } else {
        const needle = val.toLowerCase()
        this.$set(
          this.filteredOptionsMap,
          key,
          allOptions.filter(opt => {
            const text = typeof opt === 'string' ? opt : (opt[optionLabel] || '')
            return String(text).toLowerCase().includes(needle)
          })
        )
      }
    })
  }

  // ========================================
  // MÉTODOS ORIGINAIS
  // ========================================

  __labelHandler (elem, params) {
    try {
      if (params || (elem.label && elem.label[0] === '$')) {
        const key = elem.label?.substring(2, elem.label.length - 1)
        return params[key] ?? elem.label ?? elem.title
      }
      return elem.label ?? elem.title
    } catch (error) {
      console.error('__labelHandler', error)
      return elem.label ?? elem.title
    }
  }

  get columns () {
    const cols = this.fields.map((col, index) => {
      // Processar label sem depender de __labelHandler
      const label = col.label || col.title || col.name || col.model
      return {
        ...col,
        label: label,
        name: col.name || col.model,
        field: col.name || col.model,
        align: col.align || 'center',
        sortable: true
      }
    })

    // Adicionar coluna de ações customizadas se habilitado
    if (this.customActionSlot) {
      const actionsCol = {
        name: 'actions',
        label: this.$gettext('ACTIONS') || 'Ações',
        field: 'actions',
        align: 'center',
        sortable: false,
        style: 'width: 100px; max-width: 100px;',
        visibility: 'show' // Garantir que a coluna seja visível
      }
      cols.push(actionsCol)
    }

    return cols
  }

  get visibleColumns () {
    return this.columns
      .filter((col) => {
        if (!col.visibility) return true
        return col.visibility && col.visibility !== 'hidden'
      })
      .map((col) => col.name || col.model)
  }

  get hiddenColumns () {
    return this.columns.filter(
      (col) => col.visibility && col.visibility === 'hidden'
    )
  }

  get selectionType () {
    if (this.multiselect) return 'multiple'
    if (this.singleselect) return 'single'
    if (this.noneselect || (!this.multiselect && !this.singleselect)) { return 'none' }
  }

  get computedHideBottom () {
    return !this.isPaginated
  }

  get hasHiddenFields () {
    return this.fields.some(
      (col) => col.visibility && col.visibility === 'hidden'
    )
  }

  get computedSeparator () {
    return (
      this.$options.propsData.separator ??
      this.$jt?.components?.jtDataTable?.separator ??
      this.separator
    )
  }

  get computedBordered () {
    return (
      this.$options.propsData.bordered ??
      this.$jt?.components?.jtDataTable?.bordered ??
      this.bordered
    )
  }

  get buttonsTop () {
    if (this.rowButtons) return []
    return this.buttons.filter((button) => {
      return !button.renderRow
    })
  }

  get buttonsRow () {
    if (!this.rowButtons) {
      return this.buttons.filter((button) => {
        return button.renderRow
      })
    }
    return this.buttons
  }

  get hasButtonRow () {
    return this.buttonsRow.length > 0
  }

  get showSelectRow () {
    return false // Sempre retornar false para esconder a coluna de seleção
  }

  get computedTableData () {
    return this.apiMode
      ? this.$refs.tableRef._data.serverData
      : this.$refs.tableRef.data
  }

  get computedProps () {
    return {
      ...this.$props,
      separator: this.computedSeparator,
      bordered: this.computedBordered,
      selection: 'none', // Forçar seleção como 'none' para desabilitar checkboxes
      hideBottom: this.computedHideBottom,
      visibleColumns: this.visibleColumns,
      columns: this.columns,
      filter: this.filter,
      hasButtonRow: this.hasButtonRow,
      noneselect: true, // Garantir que noneselect está true
      singleselect: false, // Garantir que singleselect está false
      multiselect: false, // Garantir que multiselect está false
      noDataText: 'Nenhum dado encontrado'
    }
  }

  get hasScopedSlots () {
    return !!Object.entries(this.$scopedSlots).length
  }

  get showExpandContentSlot () {
    return true // Habilitar sempre a expansão de conteúdo
  }

  get hasCustomExpandContent () {
    return this.$scopedSlots['custom-expand-content']
  }

  get searchInputs () {
    if (!this.forms || !Array.isArray(this.forms)) {
      return []
    }

    const searchInputs = []
    this.forms.forEach((form, formIndex) => {
      if (form.inputs && Array.isArray(form.inputs)) {
        form.inputs.forEach((input, inputIndex) => {
          // Filtrar apenas inputs que não são datatables
          if (input.type !== 'DATATABLE') {
            searchInputs.push(input)
          }
        })
      }
    })

    return searchInputs
  }
}
export default ModalFiltrosDinamicos
</script>

<style>
/* ========================================
   ESTILOS NÃO-SCOPED PARA CÉLULA DE AÇÕES
   ======================================== */

/* Forçar hover da tabela no TR */
.results-section .q-table tbody tr {
  background-color: white !important;
  transition: background-color 0.3s ease !important;
}

.results-section .q-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

/* Todas as células sempre transparentes */
.results-section .q-table tbody tr td {
  background-color: transparent !important;
  background: transparent !important;
}

.results-section .q-table tbody tr:hover td {
  background-color: transparent !important;
  background: transparent !important;
}

/* Célula de ações customizada */
.custom-actions-cell {
  padding: 0 !important;
  background-color: transparent !important;
  background: transparent !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  text-align: center !important;
  vertical-align: middle !important;
}

.custom-actions-cell:hover {
  background-color: transparent !important;
  background: transparent !important;
}

.results-section .q-table tbody tr:hover .custom-actions-cell {
  background-color: transparent !important;
  background: transparent !important;
}

.custom-actions-cell .q-btn {
  margin: 0 !important;
  background-color: transparent !important;
  background: transparent !important;
}

.custom-actions-cell .q-btn:hover {
  background-color: transparent !important;
  background: transparent !important;
}

.custom-actions-cell .q-btn::before,
.custom-actions-cell .q-btn::after,
.custom-actions-cell .q-focus-helper,
.custom-actions-cell .q-ripple {
  display: none !important;
}

/* Garantir que o TR controla o hover */
.jt-datatable .q-table tbody tr {
  background-color: white !important;
}

.jt-datatable .q-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.jt-datatable .q-table tbody tr td {
  background-color: transparent !important;
}

.jt-datatable .q-table tbody tr:hover td {
  background-color: transparent !important;
}

.jt-datatable .q-table tbody tr:hover .custom-actions-cell {
  background-color: transparent !important;
}

</style>

<style lang="stylus" scoped>
@import '~quasar-variables';
// ========================================
// ESTILOS DA MODAL (PADRÃO DO SISTEMA)
// ========================================
.q-dialog
  .q-dialog__inner
    padding: 16px

.modal-card
  width: 1420px !important
  min-width: 1420px !important
  min-height: 600px !important
  max-width: 1420px !important
  height: 900px
  max-height: 900px
  padding-top: 16px
  display: flex
  flex-direction: column
  overflow: hidden
  gap: 20px
  opacity: 1
  transform: rotate(0deg)
  background: #FFF

.modal-header
  flex-shrink: 0

.header
  height: 50px
  padding: 8px 20px
  border-radius: 4px
  margin: 0px 10px
  color: white !important
  flex-shrink: 0
  background-color: #1E4971

.header-content-wrapper
  height: 34px
  gap: 20px
  display: flex
  align-items: center
  justify-content: space-between

.header-title
  height: 34px
  font-family: Roboto
  font-weight: 700
  font-style: Bold
  font-size: 28px
  line-height: 34px

.header-close-btn
  flex-shrink: 0
  cursor: pointer

.modal-body
  flex: 1
  overflow: auto
  display: flex
  flex-direction: column
  padding: 0
  background: transparent

// ========================================
// SEÇÃO DE PESQUISA
// ========================================

.search-section
  border-bottom: 1px solid #e0e0e0
  flex-shrink: 0

.section-title
  font-size: 16px
  font-weight: 500
  margin: 0
  padding: 5px 24px 8px
  color: #333

// ========================================
// FILTRO DE DIAS (CHECKBOXES)
// ========================================

.day-filter-section
  display: flex
  align-items: center
  gap: 16px
  padding: 12px 16px 12px 0px
  flex-shrink: 0

.day-filter-label
  font-family: Roboto
  font-size: 18px
  font-weight: 500
  color: #707070
  white-space: nowrap
  line-height: 1.2

.day-filter-checkboxes
  display: flex
  gap: 8px
  align-items: center

  ::v-deep .q-checkbox
    .q-checkbox__label
      font-size: 13px
      color: #555

.search-form
  padding: 10px 24px
  display: flex
  flex-direction: column
  gap: 16px
  align-items: center

.search-fields
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr))
  gap: 16px
  width: 100%
  margin-bottom: 16px

.search-field
  width: 100%
  min-width: 0

  .search-input
    width: 100% !important

  .q-input, .q-select, .q-textarea
    width: 100% !important

    .q-field__control
      padding-top: 8px
      padding-bottom: 8px

    .q-field__native
      padding-left: 0px
      padding-top: 20px
      min-height: 24px

    .q-field__label
      padding-left: 0px
      top: 16px
      font-size: 16px
      line-height: 1
      transform-origin: left center
      transition: all 0.36s cubic-bezier(0.4, 0, 0.2, 1)
      color: rgba(0, 0, 0, 0.6)

    // Estilo para campos standard (sem borda)
    &:not(.q-field--outlined):not(.q-field--filled)
      .q-field__control
        padding-left: 0px
        padding-right: 0px

      /deep/.q-field__control:before
        border-bottom: 1px solid rgba(0, 0, 0, 0.24) !important
        border-bottom-style: solid !important
        left: 0
        right: 0

      .q-field__control:hover:before
        border-bottom: 2px solid rgba(0, 0, 0, 0.87)

      &.q-field--focused .q-field__control:before
        border-bottom: 2px solid $primary

      &.q-field--error .q-field__control:before
        border-bottom: 2px solid $negative

      // Label flutuante para campos standard
      &.q-field--focused .q-field__label,
      &.q-field--float .q-field__label
        top: 8px
        font-size: 12px
        transform: translateY(0) scale(1)
        color: $primary

      &.q-field--error .q-field__label
        color: $negative

    // Ajustes específicos para q-select
    &.q-select
      .q-field__native
        padding-top: 20px

      .q-field__append
        padding-top: 8px

    // Ajustes para campos de data
    &.q-input--mask
      .q-field__native
        padding-top: 20px

  // Estilo específico para campo de date range
  .date-range-input
    .q-field__control:before
      border-style: solid !important

.search-actions
  display: flex
  justify-content: center
  width: 100%
  margin-top: 8px

.search-btn
  min-width: 129px
  height: 36px
  gap: 8px
  opacity: 1
  border-radius: 4px !important
  border: 1px solid #E9E6F4 !important
  padding: 0px 16px !important
  background: #FC855F !important
  color: white !important
  font-weight: 500 !important
  box-shadow: 0px 1px 3px 1px #00000026, 0px 1px 2px 0px #0000004D !important
  transition: all 300ms ease-in !important

  &:hover
    transform: translateY(-1px)
    box-shadow: 0px 2px 6px 2px #00000026, 0px 2px 4px 0px #0000004D !important

  .q-icon
    margin-right: 4px

// ========================================
// SEÇÃO DE RESULTADOS
// ========================================

.results-section
  flex: 1
  padding: 0 24px 24px
  display: flex
  flex-direction: column
  min-height: 0
  overflow-y: auto
  background: transparent
  width: 100%

  .section-title
    padding: 16px 0 8px

// ========================================
// ESTILOS DA TABELA (ORIGINAIS ADAPTADOS)
// ========================================

/deep/.jt-datatable
  flex: 1
  min-height: 0
  width: 100%

  .q-table
    width: 100%

  .q-table__container
    max-height: 500px
    overflow: auto
    width: 100%

  .q-table__middle
    width: 100%

  table
    width: 100% !important

  // ============================================
  // HOVER E BACKGROUND - APLICAR NO TR
  // ============================================

  tbody tr
    background-color: white !important
    transition: background-color 0.3s ease

    &:hover
      background-color: #f5f5f5 !important

  // ============================================
  // CÉLULAS - SEMPRE TRANSPARENTES
  // ============================================

  tbody tr td
    background-color: transparent !important
    background: transparent !important
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important

    &:hover
      background-color: transparent !important
      background: transparent !important

  // ============================================
  // CÉLULA DE AÇÕES - ESTILOS ESPECÍFICOS
  // ============================================

  tbody tr td[class*="body-cell-actions"],
  tbody tr td[class*="actions"],
  tbody tr td.custom-actions-cell
    text-align: center !important
    vertical-align: middle !important
    padding: 0 !important
    background-color: transparent !important
    background: transparent !important
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important

    &:hover
      background-color: transparent !important
      background: transparent !important

    // Container wrapper do slot
    > div
      display: flex !important
      align-items: center !important
      justify-content: center !important
      width: 100% !important
      min-height: 40px !important
      padding: 8px !important
      background: transparent !important

    // Botão dentro da célula
    .q-btn
      background-color: transparent !important
      background: transparent !important
      margin: 0 !important

      &:hover
        background-color: transparent !important
        background: transparent !important

      &:before,
      &:after
        display: none !important

      .q-focus-helper
        display: none !important

      .q-ripple
        display: none !important

  .hidden-row
    label
      font-weight: bold

      &:after
        content: '  '

    div
      text-align: left
      padding: 0px 10px

  th, td
    text-align: center
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    font-size: 12px !important
    padding: 4px 8px !important
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important
    vertical-align: middle !important

  thead tr th
    position: sticky !important
    top: 0
    z-index: 2
    background-color: #4A90E2 !important
    color: white !important
    font-weight: 500 !important
    height: 40px
    font-size: 11px !important
    border-bottom: 2px solid rgba(255, 255, 255, 0.3) !important
    text-align: center !important
    vertical-align: middle !important

  .q-table__bottom, thead tr
    box-shadow: 0 1px 0 0 #ddd, 0 -5px 0 0 #fff

  .q-table thead tr
    height: 40px
    text-transform: uppercase
    background-color: #4A90E2 !important
    color: white !important
    font-weight: 500
    position: sticky !important
    top: 0
    z-index: 2

  // Tabela com largura total sem reordenamento
  .q-table
    width: 100%

    thead tr, tbody tr
      width: 100%

    th, td
      text-align: center
      padding: 4px 8px

    // Primeira coluna (botão de expansão) um pouco menor
    th:first-child, td:first-child
      width: 60px
      min-width: 60px
      max-width: 60px

  // Estilos para o botão de expansão
  .q-table tbody tr td:first-child .q-btn
    min-width: 30px !important
    width: 30px !important
    height: 30px !important
    border-radius: 4px !important
    background-color: #4A90E2 !important
    color: white !important

    &:hover
      background-color: #357ABD !important

    .q-icon
      font-size: 16px !important

// ========================================
// ESTILOS GLOBAIS PARA CÉLULA DE AÇÕES
// ========================================

// Forçar hover uniforme - background no TR
::v-deep .q-table tbody tr {
  background-color: white !important;
}

::v-deep .q-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

// Forçar todas as células transparentes
::v-deep .q-table tbody tr td {
  background-color: transparent !important;
  background: transparent !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

::v-deep .q-table tbody tr:hover td {
  background-color: transparent !important;
  background: transparent !important;
}

// Estilos específicos para células de ações
::v-deep .q-table tbody tr td[class*="actions"],
::v-deep .q-table tbody tr td[class*="body-cell-actions"],
::v-deep .q-table tbody tr td.custom-actions-cell,
::v-deep .results-section .q-table tbody tr td[data-col="actions"] {
  text-align: center !important;
  vertical-align: middle !important;
  padding: 0 !important;
  background-color: transparent !important;
  background: transparent !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

// Wrapper dentro da célula de ações
::v-deep .q-table tbody tr td[class*="actions"] > div,
::v-deep .q-table tbody tr td[class*="body-cell-actions"] > div,
::v-deep .q-table tbody tr td.custom-actions-cell > div {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  min-height: 40px !important;
  padding: 8px !important;
  background: transparent !important;
}

// Botões dentro da célula de ações
::v-deep .q-table tbody tr td[class*="actions"] .q-btn,
::v-deep .q-table tbody tr td[class*="body-cell-actions"] .q-btn,
::v-deep .q-table tbody tr td.custom-actions-cell .q-btn {
  margin: 0 !important;
  background-color: transparent !important;
  background: transparent !important;
}

::v-deep .q-table tbody tr td[class*="actions"] .q-btn:hover,
::v-deep .q-table tbody tr td[class*="body-cell-actions"] .q-btn:hover,
::v-deep .q-table tbody tr td.custom-actions-cell .q-btn:hover {
  background-color: transparent !important;
  background: transparent !important;
}

::v-deep .q-table tbody tr td[class*="actions"] .q-btn::before,
::v-deep .q-table tbody tr td[class*="actions"] .q-btn::after,
::v-deep .q-table tbody tr td[class*="body-cell-actions"] .q-btn::before,
::v-deep .q-table tbody tr td[class*="body-cell-actions"] .q-btn::after,
::v-deep .q-table tbody tr td.custom-actions-cell .q-btn::before,
::v-deep .q-table tbody tr td.custom-actions-cell .q-btn::after,
::v-deep .q-table tbody tr td[class*="actions"] .q-focus-helper,
::v-deep .q-table tbody tr td[class*="body-cell-actions"] .q-focus-helper,
::v-deep .q-table tbody tr td.custom-actions-cell .q-focus-helper {
  display: none !important;
}

// ========================================
// RESPONSIVIDADE
// ========================================

// Apenas para telas muito pequenas onde 1420px não cabe
@media (max-width: 1450px)
  .modal-card
    width: 90vw !important
    height: 90vh !important
    min-width: auto !important
    min-height: auto !important
    max-width: 1420px !important
    max-height: 900px !important

@media (max-width: 768px)
  .modal-card
    width: 95vw !important
    height: 95vh !important
    min-width: auto !important
    min-height: auto !important

  .search-fields
    grid-template-columns: 1fr !important
    gap: 12px

  .search-form
    padding: 0 16px 16px

  .results-section
    padding: 0 16px 16px

@media (max-width: 1200px)
  .search-fields
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important

@media (max-width: 900px)
  .search-fields
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)) !important

// ========================================
// MODAL FOOTER
// ========================================

.modal-footer
  flex-shrink: 0

.footer
  height: 60px
  gap: 20px
  padding: 12px 0
  background: #FFFFFF
  display: flex
  justify-content: center
  align-items: center
  flex-shrink: 0

.btn-fechar
  width: 120px

// Esconder coluna de expansão quando hideExpandButton é true
.hide-expand-column
  ::v-deep .q-table
    th:last-child,
    td:last-child
      display: none !important
</style>
